import api from 'lib/trpcClient';
import { Suspense } from 'react';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import { CurrencyCode, Vehicle } from 'further-types/lp/vehicle';
import { RevaluationType } from 'further-types/lp/revaluation';
import FormWrapper from '../FormWrapper';
import RevaluationForm from '../CreateRevaluation';
import { useRevaluationForm } from '../hooks/useRevaluationForm';
import useRevaluationFormSync from '../hooks/useRevaluationFormSync';
import { AssetFullHistoryItem } from '../FormWrapper';
import RevaluationRowSkeleton from './RevaluationRowSkeleton';
import { revaluation as rules } from 'further-ui/validations';

type CompanyRow = {
  _id: string;
  assetId: string;
  name: string;
  companyLogo?: string | null;
};

type Props = {
  company: CompanyRow;
  vehicleId: string;
  vehicle: Vehicle;
  savedData?: rules.RevaluationFormType;
  periodEndDate?: Date;
  initialTab?: 'total' | 'perClass';
  onTabChange: (companyId: string, tab: 'total' | 'perClass') => void;
  onDataChange: (
    companyId: string,
    data: rules.RevaluationFormType,
    assetId: string,
    assetName: string,
    name: string,
    companyLogo?: string | null,
  ) => void;
  globalFxRates: Record<string, CurrencyRateDetails>;
};

const CompanyRevaluationRowContent: React.FC<Props> = ({
  company,
  vehicleId,
  vehicle,
  savedData,
  periodEndDate,
  initialTab,
  onTabChange,
  onDataChange,
  globalFxRates,
}) => {
  const [
    [
      asset,
      revaluations,
      assetFullHistory,
      holdingClassInvestmentTotals,
      investmentHistory,
    ],
  ] = api.useSuspenseQueries((t) => [
    t.asset.get({ assetId: company.assetId, vehicleId }),
    t.asset.getAssetRevaluations({ assetId: company.assetId, vehicleId }),
    t.asset.getAssetFullHistory({ assetId: company.assetId, vehicleId }),
    t.asset.getAssetHoldingClassInvestmentTotals({
      assetId: company.assetId,
      vehicleId,
    }),
    t.asset.getInvestmentHistory({
      assetId: company.assetId,
      vehicleId,
      order: 'desc',
      orderBy: 'dealCompletionDate',
    }),
  ]);

  const newRevaluations = holdingClassInvestmentTotals.map(
    (holdingClassInvestmentTotal) => {
      const holdingClassId = holdingClassInvestmentTotal.holdingClassId;
      const holdingClassHistory: Array<AssetFullHistoryItem> =
        assetFullHistory[holdingClassId] ?? [];

      return {
        id: holdingClassId,
        revaluationId: undefined,
        holdingClassId,
        type: RevaluationType.Revaluation as const,
        assetId: company.assetId,
        holdingClassName: holdingClassHistory[0]?.holdingClassName ?? '',
        unitPriceTcy: holdingClassInvestmentTotal.latestUnitPrice ?? 0,
        currentHoldValue: holdingClassInvestmentTotal.currentHoldValue ?? 0,
        totalNumberOfUnits: holdingClassInvestmentTotal.totalNumberOfUnits ?? 0,
        currentNumberOfUnits:
          holdingClassInvestmentTotal.totalNumberOfUnits ?? 0,
        ledgerDate: periodEndDate ?? new Date(),
        currency:
          holdingClassInvestmentTotal.currency ??
          asset.baseCurrency ??
          CurrencyCode.GBP,
        isRemoved: false,
        isNew: true,
        currentHoldValueLcy:
          holdingClassInvestmentTotal.currentHoldValueLcy ?? undefined,
      };
    },
  );

  const formMethods = useRevaluationForm({
    investmentHistory,
    revaluations,
    asset,
    newRevaluations,
    holdingClassInvestmentTotals,
    initialRevaluationDate: periodEndDate,
  });

  useRevaluationFormSync(formMethods, savedData, (data) =>
    onDataChange(
      company._id,
      data,
      company.assetId,
      asset.name,
      company.name,
      company.companyLogo,
    ),
  );

  const assetCurrency = asset.baseCurrency;

  return (
    <FormWrapper
      formMethods={formMethods}
      externalState={{ selectedVehicle: vehicle, assetFullHistory }}
    >
      <RevaluationForm
        assetName={asset.name}
        assetCurrency={assetCurrency}
        assetId={company.assetId}
        vehicleId={vehicleId}
        vehicle={vehicle}
        holdingClassInvestmentTotals={holdingClassInvestmentTotals}
        onSuccess={() => undefined}
        renderButtons={() => null}
        initialTab={initialTab}
        onTabChange={(tab) => onTabChange(company._id, tab)}
        tabbed
        disableDate
        fxRate={globalFxRates[assetCurrency] ?? null}
      />
    </FormWrapper>
  );
};

const CompanyRevaluationRow: React.FC<Props> = (props) => (
  <Suspense fallback={<RevaluationRowSkeleton />}>
    <CompanyRevaluationRowContent {...props} />
  </Suspense>
);

export default CompanyRevaluationRow;
